import type { KeyboardEvent } from 'react';

/**
 * The Send key (spec §3, preference `send_key`): `ctrl_enter` (the proposed
 * default) sends with Ctrl+Enter, ⌘+Enter on a Mac, and Enter adds a line;
 * `enter` sends with Enter and Shift+Enter adds a line. An Enter that
 * belongs to an IME composition never sends.
 */

export type SendKey = 'ctrl_enter' | 'enter';

export type KeyEventLike = Pick<
  KeyboardEvent,
  'key' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey' | 'keyCode'
> & { nativeEvent?: { isComposing?: boolean } };

/**
 * Safari fires the Enter that confirms a composition after compositionend
 * (with isComposing already false): an Enter this soon after it is part of
 * the composition.
 */
export const COMPOSITION_GRACE_MS = 50;

/**
 * Whether this key press sends: never while an IME composes (isComposing,
 * keyCode 229, and Safari's Enter right after compositionend).
 */
export const isSendKey = (
  event: KeyEventLike,
  sendKey: SendKey,
  justComposed: boolean,
) => {
  if (event.key !== 'Enter') return false;
  if (event.nativeEvent?.isComposing || event.keyCode === 229 || justComposed) {
    return false;
  }
  const modified = event.ctrlKey || event.metaKey;
  if (sendKey === 'enter') return !event.shiftKey && !modified && !event.altKey;
  return modified && !event.shiftKey && !event.altKey;
};

/** Whether the keyboard has ⌘ rather than Ctrl as its command key. */
export const isApplePlatform = (
  nav:
    | { platform?: string; userAgent?: string }
    | undefined = typeof navigator === 'undefined' ? undefined : navigator,
) =>
  /Mac|iPhone|iPad|iPod/i.test(
    `${nav?.platform ?? ''} ${nav?.userAgent ?? ''}`,
  );

/** The help text under the field follows the active setting. */
export const sendHelp = (sendKey: SendKey, apple: boolean) =>
  sendKey === 'enter'
    ? { key: 'v2.composer.helpEnter', values: {} }
    : {
        key: 'v2.composer.helpCtrlEnter',
        values: { mod: apple ? '⌘' : 'Ctrl' },
      };
